import React from "react"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { setState, setModalData } from "@/store/globalSlice"
import ModalCloseButton from "@/Components/ModalCloseButton"
import { ModalTopAccent } from "@/Components/Modal"

interface ThemeSwitcherModalProps { }

const ThemeSwitcherModal: React.FC<ThemeSwitcherModalProps> = () => {
  const dispatch = useAppDispatch()
  const selectedTheme = useAppSelector((state) => state.global.selectedTheme)
  const modalData = useAppSelector((state) => state.global.modalData)

  // List of available background themes
  const themes = [
    { id: "default", name: "Default", image: "lobby-background1.png" },
    { id: "theme2", name: "Theme 2", image: "lobby-background2.png" },
    { id: "theme3", name: "Theme 3", image: "lobby-background3.png" },
    { id: "theme4", name: "Theme 4", image: "lobby-background4.png" },
    { id: "theme5", name: "Theme 5", image: "lobby-background5.png" },
    { id: "theme6", name: "Theme 6", image: "lobby-background6.png" },
    { id: "theme7", name: "Theme 7", image: "lobby-background7.png" },
    { id: "theme8", name: "Theme 8", image: "lobby-background8.png" },
    { id: "theme9", name: "Theme 9", image: "lobby-background9.png" },
    { id: "theme10", name: "Theme 10", image: "lobby-background10.png" },
    { id: "theme11", name: "Theme 11", image: "lobby-background11.png" },
    { id: "theme12", name: "Theme 12", image: "lobby-background12.png" },
    { id: "theme13", name: "Theme 13", image: "lobby-background13.png" },
    { id: "theme14", name: "Theme 14", image: "lobby-background14.png" },
    { id: "theme15", name: "Theme 15", image: "lobby-background15.png" },
    { id: "theme16", name: "Theme 16", image: "lobby-background16.png" },
    { id: "theme17", name: "Theme 17", image: "lobby-background17.png" },
    { id: "theme18", name: "Theme 18", image: "lobby-background18.png" },
    { id: "theme19", name: "Theme 19", image: "lobby-background19.png" },
    { id: "theme20", name: "Theme 20", image: "lobby-background20.png" },
    { id: "theme21", name: "Theme 21", image: "lobby-background21.png" },
    { id: "theme22", name: "Theme 22", image: "lobby-background22.png" },
    { id: "theme23", name: "Theme 23", image: "lobby-background23.png" },
    { id: "theme24", name: "Theme 24", image: "lobby-background24.png" },
    { id: "theme25", name: "Theme 25", image: "lobby-background25.png" },
    { id: "theme26", name: "Theme 26", image: "lobby-background26.png" },
    { id: "theme27", name: "Theme 27", image: "lobby-background27.png" },
    { id: "theme28", name: "Theme 28", image: "lobby-background28.png" },
    // { id: "theme29", name: "Theme 29", image: "lobby-background29.png" },

    { id: "theme33", name: "Theme 33", image: "lobby-background33.png" },
    { id: "theme34", name: "Theme 34", image: "lobby-background34.png" },
    { id: "theme35", name: "Theme 35", image: "lobby-background35.png" },
    { id: "theme36", name: "Theme 36", image: "lobby-background36.png" },
    { id: "theme37", name: "Theme 37", image: "lobby-background37.png" },
    { id: "theme38", name: "Theme 38", image: "lobby-background38.png" },
  ]

  // Get current theme index
  const currentThemeIndex = themes.findIndex(
    (theme) => theme.id === selectedTheme?.id
  )
  const [selectedIndex, setSelectedIndex] = React.useState(
    currentThemeIndex >= 0 ? currentThemeIndex : 0
  )

  // Update selected index when theme changes externally
  React.useEffect(() => {
    const newIndex = themes.findIndex(
      (theme) => theme.id === selectedTheme?.id
    )
    if (newIndex >= 0) {
      setSelectedIndex(newIndex)
    }
  }, [selectedTheme?.id])

  const handleThemeSelect = (themeId: string) => {
    const theme = themes.find((theme) => theme.id === themeId)
    if (theme) {
      dispatch(setState({ selectedTheme: theme }))
    }
  }

  const handleClose = () => {
    dispatch(setModalData({ themeSwitcher: { visible: false } }))
  }

  // Handle keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!modalData.themeSwitcher?.visible) return

      switch (event.key) {
        case "ArrowLeft":
          event.preventDefault()
          setSelectedIndex((prev) => {
            const newIndex = prev > 0 ? prev - 1 : themes.length - 1
            handleThemeSelect(themes[newIndex].id)
            return newIndex
          })
          break
        case "ArrowRight":
          event.preventDefault()
          setSelectedIndex((prev) => {
            const newIndex = prev < themes.length - 1 ? prev + 1 : 0
            handleThemeSelect(themes[newIndex].id)
            return newIndex
          })
          break
        case "ArrowUp":
          event.preventDefault()
          setSelectedIndex((prev) => {
            const newIndex = prev >= 4 ? prev - 4 : prev + themes.length - 4
            handleThemeSelect(themes[newIndex].id)
            return newIndex
          })
          break
        case "ArrowDown":
          event.preventDefault()
          setSelectedIndex((prev) => {
            const newIndex =
              prev + 4 < themes.length ? prev + 4 : prev - themes.length + 4
            handleThemeSelect(themes[newIndex].id)
            return newIndex
          })
          break
        case "Escape":
          event.preventDefault()
          handleClose()
          break
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => {
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [modalData.themeSwitcher?.visible, themes, dispatch])

  if (!modalData.themeSwitcher?.visible) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="relative w-full max-w-4xl mx-4 max-h-[80vh] overflow-y-auto rounded-2xl border border-gray-700/60 bg-black/90 backdrop-blur-xl shadow-2xl p-6">
        <ModalTopAccent />
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-white">Choose Theme</h2>
          <ModalCloseButton onClick={handleClose} />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {themes.map((theme, index) => (
            <div
              key={theme.id}
              className={`border-2 rounded-lg p-3 cursor-pointer transition-all hover:scale-105 ${selectedIndex === index
                  ? "border-amber-400 bg-amber-400/10 ring-2 ring-amber-400/40"
                  : "border-white/10 hover:border-white/30"
                }`}
              onClick={() => {
                setSelectedIndex(index)
                handleThemeSelect(theme.id)
              }}
            >
              <div
                className="aspect-video bg-cover bg-center rounded mb-2"
                style={{
                  backgroundImage: `url(/src/images/backgrounds/${theme.image})`,
                }}
                onError={(e) => {
                  // Fallback to default background if image fails to load
                  e.currentTarget.style.backgroundImage =
                    "url(/src/images/backgrounds/lobby-background.webp)"
                }}
              ></div>
              <p className="text-sm font-medium text-center text-white/80">
                {theme.name}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-white/40">
            Use arrow keys to navigate, Enter to select, or Ctrl+Q to open this
            dialog anytime
          </p>
        </div>
      </div>
    </div>
  )
}

export default ThemeSwitcherModal
